import React, { useRef } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { graphqlApi } from '../../../services/graphql';

/**
 * Section 3 of the 5-section campaign registration layout.
 *
 * Scope: representative images, product/service long-form introduction,
 * provision method, visit notes, and reference file attachments. These
 * fields lived in the monolithic `ProductServiceInfoV2` previously —
 * splitting them into this "상세정보" card follows the reference layout
 * (see frontend/tmp/campaign_register_layout.html §3).
 *
 * Field names (data model) are unchanged:
 * `representativeImages`, `productIntroduction`, `provisionMethod`,
 * `additionalNotes`, `attachments`.
 */
const MAX_IMAGES = 20;
const MAX_SIZE_MB = 10;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

async function resizeImage(file: File, maxBytes: number): Promise<File> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const ratio = Math.sqrt(maxBytes / file.size);
      if (ratio < 1) {
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          resolve(new File([blob!], file.name, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        0.85,
      );
    };
    img.src = url;
  });
}

interface Props {
  control: Control<any>;
  setValue: any;
  watch: any;
}

const CampaignDetailV2: React.FC<Props> = ({ control, setValue, watch }) => {
  const { t } = useTranslation(['campaigns', 'common']);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const images: any[] = watch('representativeImages') || [];
  const attachments: any[] = watch('attachments') || [];

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (images.length + files.length > MAX_IMAGES) {
      alert(t('campaigns:registration.v2.maxImagesError', { max: MAX_IMAGES }));
      return;
    }
    const newImages: any[] = [];
    for (let file of files) {
      if (file.size > MAX_SIZE_BYTES) {
        file = await resizeImage(file, MAX_SIZE_BYTES);
      }
      try {
        const uploadRes = await graphqlApi.getUploadUrl(
          file.name,
          file.type,
          file.size,
        );
        if (uploadRes.success && uploadRes.data) {
          await fetch(uploadRes.data.uploadUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
          });
          newImages.push({
            fileKey: uploadRes.data.fileKey,
            fileUrl: uploadRes.data.fileUrl,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
          });
        }
      } catch (err) {
        console.error('Image upload failed:', err);
      }
    }
    setValue('representativeImages', [...images, ...newImages]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index: number) => {
    setValue(
      'representativeImages',
      images.filter((_: any, i: number) => i !== index),
    );
  };

  const handleAttachmentUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(e.target.files || []);
    const newAttachments: any[] = [];
    for (const file of files) {
      try {
        const uploadRes = await graphqlApi.getUploadUrl(
          file.name,
          file.type,
          file.size,
          true,
        );
        if (uploadRes.success && uploadRes.data) {
          await fetch(uploadRes.data.uploadUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
          });
          newAttachments.push({
            fileKey: uploadRes.data.fileKey,
            fileUrl: uploadRes.data.fileUrl,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
          });
        }
      } catch (err) {
        console.error('Attachment upload failed:', err);
      }
    }
    setValue('attachments', [...attachments, ...newAttachments]);
    if (attachmentInputRef.current) attachmentInputRef.current.value = '';
  };

  return (
    <div className="space-y-4">
      {/* 대표사진 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('campaigns:registration.v2.representativeImages')}
          <span className="ml-1 text-xs text-gray-400">
            ({images.length}/{MAX_IMAGES})
          </span>
        </label>
        <div className="flex flex-wrap gap-2 mb-2">
          {images.map((img: any, i: number) => (
            <div
              key={i}
              className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200"
            >
              <img
                src={img.fileUrl}
                alt=""
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => removeImage(i)}
                className="absolute top-0 right-0 bg-black bg-opacity-50 rounded-bl p-0.5"
              >
                <XMarkIcon className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
          {images.length < MAX_IMAGES && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-blue-400 transition-colors"
            >
              <PhotoIcon className="w-6 h-6 text-gray-400" />
            </button>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleImageUpload}
        />
        <p className="text-xs text-gray-400">
          {t('campaigns:registration.v2.imageHint', {
            max: MAX_IMAGES,
            size: MAX_SIZE_MB,
          })}
        </p>
      </div>

      <hr className="border-gray-100" />

      {/* 제품/서비스 소개 */}
      <Controller
        name="productIntroduction"
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('campaigns:registration.v2.productIntroduction')}{' '}
              <span className="text-red-500">*</span>
            </label>
            <textarea
              {...field}
              rows={6}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:outline-none ${
                fieldState.error
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
            />
          </div>
        )}
      />

      {/* 제공 방식 */}
      <Controller
        name="provisionMethod"
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('campaigns:registration.v2.provisionMethod')}{' '}
              <span className="text-red-500">*</span>
            </label>
            <textarea
              {...field}
              rows={3}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:outline-none ${
                fieldState.error
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
            />
          </div>
        )}
      />

      {/* 방문 및 안내사항 */}
      <Controller
        name="additionalNotes"
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('campaigns:registration.v2.visitNotes')}{' '}
              <span className="text-red-500">*</span>
            </label>
            <textarea
              {...field}
              rows={3}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:outline-none ${
                fieldState.error
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
            />
          </div>
        )}
      />

      {/* 참고 자료 링크 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('campaigns:registration.v2.referenceFiles')}
        </label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => attachmentInputRef.current?.click()}
            className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50"
          >
            {t('campaigns:registration.v2.attachFile')}
          </button>
          {attachments.length > 0 && (
            <span className="text-sm text-green-600">
              {attachments.length} {t('campaigns:registration.v2.filesAttached')}
            </span>
          )}
        </div>
        <input
          ref={attachmentInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleAttachmentUpload}
        />
        {attachments.length > 0 && (
          <ul className="mt-2 space-y-1">
            {attachments.map((file: any, i: number) => (
              <li
                key={file.fileKey || i}
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded border border-gray-200 text-xs text-gray-600"
              >
                <span className="truncate flex-1">{file.fileName}</span>
                <button
                  type="button"
                  onClick={() => {
                    setValue(
                      'attachments',
                      attachments.filter((_: any, idx: number) => idx !== i),
                    );
                  }}
                  className="text-gray-400 hover:text-red-500 flex-shrink-0"
                >
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CampaignDetailV2;
